import { TreeTableProps } from './types';

export interface TreeTableState {
  partiallySelectedRows?: ReadonlyArray<string> | undefined;
  collapsedSelectedRows?: ReadonlyArray<string>;
  selectedRows?: ReadonlySet<string> | null;
  selectAll?: boolean | 'mixed';
}

export type TreeTableAction =
  | {
      type: 'parentChildSelection';
      rowKey: string;
      props: TreeTableProps;
      selected?: boolean | 'mixed';
      selectedRows?: ReadonlySet<string> | null;
      selectAll?: boolean | 'mixed';
      interactive?: boolean;
      initialSelectedRows?: ReadonlySet<string> | null;
      initialSelectAll?: boolean | 'mixed';
    }
  | {
      type: 'selectRow';
      selectedRows?: ReadonlySet<string> | null;
      selectAll?: boolean | 'mixed';
      interactive?: boolean;
    }
  | {
      type: 'clearPartiallySelectedRows';
    };

const reducer = (prevState: TreeTableState, action: TreeTableAction) => {
  switch (action.type) {
    case 'selectRow': {
      const selectedRows = new Set<string>(action.selectedRows);
      return {
        ...prevState,
        selectedRows,
        selectAll: action.selectAll,
      };
    }
    case 'clearPartiallySelectedRows': {
      return {
        ...prevState,
        partiallySelectedRows: [],
      };
    }
    case 'parentChildSelection': {
      const { rowKey, selected, selectedRows, selectAll, initialSelectedRows } = action;
      const {
        getDataNode,
        getParentsRowKey,
        getRowIndex,
        getChildNodesRowKey,
        isRowReadOnly,
      } = action.props;
      if (!selectedRows || selectedRows.size === 0 || !getDataNode) {
        // no need to process parent-child selection when select all is clicked
        return {
          ...prevState,
          partiallySelectedRows: [],
          selectedRows: new Set([]),
          selectAll: selectAll ?? false,
        };
      }
      let partiallySelectedRows: Array<string> = [...(prevState.partiallySelectedRows || [])];
      const selectedIndex = getRowIndex && rowKey ? getRowIndex(rowKey) : -1;
      const node = selectedIndex !== -1 && getDataNode(selectedIndex);
      if (!node) return { ...prevState };
      const newSelections = new Set<string>(selectedRows);
      if (selected) {
        selectedRows?.forEach((selectedRowKey) => {
          const rowIndex = getRowIndex && rowKey ? getRowIndex(selectedRowKey) : -1;
          const rowNode = selectedIndex !== -1 && getDataNode(rowIndex);
          // handle selection/unselection of parent
          newSelections.add(selectedRowKey);
          if (rowNode && rowNode.hasChildren) {
            const childRows =
              getChildNodesRowKey && selectedRowKey
                ? getChildNodesRowKey(selectedRowKey)
                : undefined;
            childRows?.forEach((childRow) => {
              newSelections.add(childRow);
            });
          }
        });
      } else {
        if (node.hasChildren && !selected) {
          const children = getChildNodesRowKey && getChildNodesRowKey(rowKey);
          children?.forEach((childRow) => {
            if (newSelections.has(childRow)) {
              if (
                !(isRowReadOnly?.(childRow) && (selectedRows?.has(childRow) || selectAll === true))
              )
                newSelections.delete(childRow);
            }
          });
        }
      }
      // handle partial selections
      const rowsToSelect = selectedRows?.size > 0 ? selectedRows : new Set([rowKey]);
      rowsToSelect.forEach((key) => {
        const parents = getParentsRowKey && getParentsRowKey(key);
        parents?.forEach((parent) => {
          let numberOfSelected = 0;
          const allChilren = getChildNodesRowKey && getChildNodesRowKey(parent);
          allChilren?.forEach((child) => {
            if (newSelections?.has(child) && parent !== child) ++numberOfSelected;
          });
          if (numberOfSelected === 0) {
            if (newSelections.has(parent)) newSelections.delete(parent);
            partiallySelectedRows.splice(partiallySelectedRows.indexOf(parent), 1);
          } else if (allChilren && allChilren?.length > numberOfSelected) {
            if (!partiallySelectedRows.includes(parent)) {
              const length = partiallySelectedRows.length;
              partiallySelectedRows[length] = parent;
            }
            if (newSelections.has(parent)) newSelections.delete(parent);
          } else {
            // all children selected
            newSelections.add(parent);
          }
        });
        partiallySelectedRows = partiallySelectedRows.filter((value) => {
          return !newSelections.has(value);
        });
      });
      const { areRowsVisible, rowCount, hideSelectAll, columnHeadersVisible } = action.props;
      const selectedRowsValidated = !areRowsVisible ? true : areRowsVisible(newSelections);

      let selectAllState: boolean | 'mixed';
      if (
        selectedRowsValidated &&
        hideSelectAll !== true &&
        rowCount !== 0 &&
        columnHeadersVisible !== false &&
        newSelections.size >= rowCount
      ) {
        selectAllState = true;
      } else if (newSelections.size > 0) {
        selectAllState = 'mixed';
      } else {
        selectAllState = false;
      }
      let updatedSelectedRows: ReadonlySet<string> | undefined = undefined;
      if (initialSelectedRows) {
        updatedSelectedRows = new Set([...newSelections, ...initialSelectedRows]);
      }

      return {
        ...prevState,
        selectedRows: updatedSelectedRows ? updatedSelectedRows : newSelections,
        selectAll: selectAllState,
        partiallySelectedRows,
      };
    }
    default:
      return prevState;
  }
};

const init = (
  selectedRows: ReadonlySet<string> | undefined | null,
  partiallySelectedRows?: ReadonlyArray<string> | undefined
): TreeTableState => {
  return {
    selectedRows,
    partiallySelectedRows,
  };
};

export const TreeTableState = {
  init,
  reducer,
} as const;
